import { useEffect, useState } from 'react';

import randomId from '@snowit/utils/randomId';

interface DomElementProps {
  // [x: string]: string;
  class?: string;
  parent?: HTMLElement;
  prefix?: string;
  id: string;
}

interface CreateDomElementProps {
  prefix?: string;
  id: string;
}

type ResolvedDomElement = { element: HTMLElement; created: boolean };

const createDomElement = (
  attributes: CreateDomElementProps,
  parent?: HTMLElement,
): ResolvedDomElement => {
  const { prefix, id, ...attrs } = attributes;
  let el = id ? document.getElementById(id) : null;
  if (el) {
    return { element: el, created: false };
  }
  el = document.createElement('div');
  el.setAttribute('id', id || randomId({ prefix }));
  for (const [key, value] of Object.entries(attrs)) {
    el.setAttribute(key, value as string);
  }

  if (parent) {
    parent.appendChild(el);
  } else {
    document.body.appendChild(el);
  }
  return { element: el, created: true };
};

/**
 * How many mounted hooks hold each element id, and whether the element was put
 * there by one of them. Shared roots (portals, notifications, badges) are
 * created by whichever consumer mounts first and found by the rest, so neither
 * "created it" nor "unmounted last" alone decides who may remove it: the node
 * goes only when the last holder leaves, and only if a hook created it.
 */
const holders = new Map<string, { count: number; owned: boolean }>();

const useDomElement = (props: DomElementProps): HTMLElement | null => {
  const { parent, ...attributes } = props;
  const [domElement, setDomElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const { element, created } = createDomElement(attributes, parent);
    setDomElement(element);

    const key = element.id;
    const holder = holders.get(key) ?? { count: 0, owned: false };
    holder.count += 1;
    holder.owned = holder.owned || created;
    holders.set(key, holder);

    return () => {
      holder.count -= 1;
      if (holder.count > 0) return;

      holders.delete(key);
      if (holder.owned) element.remove();
    };
  }, []);

  return domElement;
};

export default useDomElement;
